import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from '@mui/material';
import { imageService } from '../../api/images';
import { galleryThumbSrc, getImageLabel } from '../../utils/galleryMedia';

interface GalleryAddExistingPhotosDialogProps {
    open: boolean;
    albumId: string;
    isSaving: boolean;
    onClose: () => void;
    onAttach: (imageIds: string[]) => void;
}

export const GalleryAddExistingPhotosDialog = ({
    open,
    albumId,
    isSaving,
    onClose,
    onAttach,
}: GalleryAddExistingPhotosDialogProps) => {
    const { t } = useTranslation();
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const { data, isLoading } = useQuery({
        queryKey: ['images', 'picker', albumId],
        queryFn: () => imageService.getAll({ page: 1, perPage: 100, sort: 'newest' }),
        enabled: open,
    });

    const availableImages = useMemo(() => {
        const images = data?.data.images ?? [];
        return images.filter((image) => !image.album_ids?.includes(albumId));
    }, [albumId, data]);

    const toggleImage = (id: string) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
        );
    };

    const handleClose = () => {
        setSelectedIds([]);
        onClose();
    };

    const handleAttach = () => {
        if (selectedIds.length === 0) {
            return;
        }
        onAttach(selectedIds);
        setSelectedIds([]);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
            <DialogTitle>{t('gallery.addExistingPhotos')}</DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                ) : availableImages.length === 0 ? (
                    <Typography color="text.secondary" py={2}>
                        {t('gallery.noPhotosToAdd')}
                    </Typography>
                ) : (
                    <Stack spacing={1} mt={1}>
                        {availableImages.map((image) => {
                            const thumb = galleryThumbSrc(image);
                            const label = getImageLabel(image) || image.id;

                            return (
                                <Stack
                                    key={image.id}
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => toggleImage(image.id)}
                                >
                                    <Checkbox
                                        checked={selectedIds.includes(image.id)}
                                        onChange={() => toggleImage(image.id)}
                                        onClick={(event) => event.stopPropagation()}
                                    />
                                    {thumb ? (
                                        <Box
                                            component="img"
                                            src={thumb}
                                            alt=""
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                            }}
                                        />
                                    ) : (
                                        <Box sx={{ width: 48, height: 48, bgcolor: 'action.hover' }} />
                                    )}
                                    <Typography variant="body2" noWrap>
                                        {label}
                                    </Typography>
                                </Stack>
                            );
                        })}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{t('common.cancel')}</Button>
                <Button
                    variant="contained"
                    onClick={handleAttach}
                    disabled={isSaving || selectedIds.length === 0}
                >
                    {isSaving ? (
                        <CircularProgress size={20} />
                    ) : (
                        t('gallery.addSelectedPhotos', { count: selectedIds.length })
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
